@if (isEmbedded$ | async) {
  <div class="flex grow overflow-auto" semerisClosableInputController>
    <router-outlet></router-outlet>
  </div>
  <!-- Overlay outlet still required so sui dropdown/select/popover/toast panels render here -->
  <sui-overlay-outlet />
} @else if (isNextBlocked()) {
  <!-- `next` host, user lacks `read:next-ui`: no shell at all, just a way back to classic -->
  <semeris-next-unavailable />
  <!-- Overlay outlet still required so background effects' toasts have somewhere to render -->
  <sui-overlay-outlet />
} @else if (isAccountUnavailable()) {
  <semeris-unavailable [retryUrl]="requestedUrl()" />
  <!-- Overlay outlet still required so the offline toast has somewhere to render -->
  <sui-overlay-outlet />
} @else if (isFullscreenRoute()) {
  <!-- Chrome-less routes (e.g. the first-time setup wizard): bare outlet, no sidebar/toolbar -->
  <main class="flex flex-1 min-h-screen">
    <router-outlet></router-outlet>
  </main>
  <!-- Overlay outlet still required so sui dropdown/select/autocomplete/popover panels render here -->
  <sui-overlay-outlet />
} @else {
  @if (appEnabled$ | async) {
    <sui-sidebar-outlet />
    <!-- Hover zone: thin strip on left edge to reveal floating sidebar (mouse only) -->
    @if (sidebarService.mode() === 'float') {
      <div
        class="fixed left-0 top-0 h-screen z-(--z-sidebar) w-1.5"
        (mouseenter)="sidebarService.floatHoverEnter()"
        (mouseleave)="sidebarService.floatHoverLeave()"
      ></div>
    }
    <main class="flex-1 flex flex-col h-screen overflow-hidden" semerisClosableInputController copyToController>
      <semeris-system-message-outlet [type]="SystemMessageType.Banner" />
      <semeris-toolbar></semeris-toolbar>
      <!-- Notes sits INSIDE main, below the toolbar, so opening it never covers the toolbar or sidebar. -->
      <div class="flex flex-1 min-h-0">
        <!-- `flex` is load-bearing: routed page hosts get their height from being stretched flex
             items here, not from a height of their own. Without it their `flex-1` is inert and
             virtual-scroll viewports collapse. -->
        <div class="relative flex flex-1 min-w-0 overflow-y-auto">
          @if (isNavigating()) {
            <progress
              class="sui-progress sui-progress-sm text-primary-500 absolute top-0 inset-x-0 w-full !rounded-none z-50"
              aria-label="Loading page"
            ></progress>
          }
          <router-outlet></router-outlet>
        </div>
        <!-- The panel inside must stay FIXED width and clipped, or it reflows instead of sliding.
             `justify-end` pins it right so it enters from the edge. Mirrors sui-dock-split-child. -->
        <div
          class="flex justify-end shrink-0 overflow-hidden transition-[flex-basis] duration-300 ease-out"
          [class.basis-0]="!notesPanelOpen()"
          [class.basis-112]="notesPanelOpen()"
        >
          @defer (when notesPanelMounted(); prefetch on idle) {
            @if (notesPanelMounted()) {
              <semeris-notes-panel></semeris-notes-panel>
            }
          }
        </div>
        <!-- Help is mutually exclusive with notes (see the app reducer), so the two slots are
             never open at once — same width, no layout jump when one hands over to the other. -->
        <div
          class="flex justify-end shrink-0 overflow-hidden transition-[flex-basis] duration-300 ease-out"
          [class.basis-0]="!helpPanelOpen()"
          [class.basis-112]="helpPanelOpen()"
        >
          @defer (when helpPanelMounted(); prefetch on idle) {
            @if (helpPanelMounted()) {
              <semeris-help-panel></semeris-help-panel>
            }
          }
        </div>
      </div>
    </main>
    <!-- Includes the peek panel outlet — do NOT add a second <sui-peek-panel-outlet />, it
         renders every open panel twice. -->
    <sui-overlay-outlet />
  }
}
